import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { base_url } from "../../utils/axiosConfig";
import * as Yup from "yup";
import { toast } from "react-toastify";

// Application Form Modal
const VendorSignup = ({ show, onHide }) => {
  const [step, setStep] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [useSameAddress, setUseSameAddress] = useState(false);
  const formSteps = [
    "Personal Details",
    "Bank Details & KYC",
    "Referral Code",
    "Terms and Conditions",
  ];

  // Form validation schema using Yup
  const validationSchema = [
    // Step 0: Personal Details and Address
    Yup.object().shape({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone number is required"),
      resAddress_line: Yup.string().required("Residential Address is required"),
      resCountry: Yup.string().required("Residential Country is required"),
      resState: Yup.string().required("Residential State is required"),
      resCity: Yup.string().required("Residential City is required"),
      resDistrict: Yup.string().required("Residential District is required"),
      resPincode: Yup.string().required("Residential Pincode is required"),
      correAddress_line: Yup.string().required(
        "Correspondence/Office Address is required",
      ),
      correCountry: Yup.string().required(
        "Correspondence/Office Country is required",
      ),
      correState: Yup.string().required(
        "Correspondence/Office State is required",
      ),
      correCity: Yup.string().required(
        "Correspondence/Office City is required",
      ),
      correDistrict: Yup.string().required(
        "Correspondence/Office District is required",
      ),
      correPincode: Yup.string().required(
        "Correspondence/Office Pincode is required",
      ),
      dob: Yup.date().required("Date of birth is required"),
      gender: Yup.string().required("Gender is required"),
      vendorType: Yup.string().required("Vendor Type is required"),
      profilePhoto: Yup.mixed().required("Profile photo is required"),
      businessName: Yup.string(),
      gstNo: Yup.string(),
    }),
    // Step 1: Bank Details and KYC
    Yup.object().shape({
      bankDetails: Yup.object().shape({
        holderName: Yup.string().notRequired().trim().nullable(),
        bankName: Yup.string().notRequired().trim().nullable(),
        accountNo: Yup.string()
          .notRequired()
          .trim()
          .nullable()
          .matches(/^\d{9,18}$/, {
            message: "Account number must be 9-18 digits",
            excludeEmptyString: true, // ← important: don't validate empty string
          }),
        ifscCode: Yup.string()
          .notRequired()
          .trim()
          .nullable()
          .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, {
            message: "Invalid IFSC code format",
            excludeEmptyString: true,
          }),
      }),
      aadharCard: Yup.mixed()
        .nullable()
        .test(
          "fileType",
          "Aadhar card must be a PDF, JPG, JPEG, or PNG",
          (value) => {
            if (!value) return true; // allow empty
            return ["application/pdf", "image/jpeg", "image/png"].includes(
              value.type,
            );
          },
        ),
      panCard: Yup.mixed()
        .required("PAN card is required")
        .test(
          "fileType",
          "PAN card must be a PDF, JPG, JPEG, or PNG",
          (value) =>
            value &&
            ["application/pdf", "image/jpeg", "image/png"].includes(value.type),
        ),
      aadharCardNo: Yup.string().matches(
        /^\d{12}$/,
        "Aadhar card number must be 12 digits",
      ),
      panCardNo: Yup.string()
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN card number format")
        .required("PAN card number is required"),
    }),
    // Step 2: Referral Code
    Yup.object().shape({
      referralCode: Yup.string(),
    }),
    // Step 4: Terms and Conditions
    Yup.object().shape({
      agreement: Yup.boolean()
        .required("You must agree to the Terms and Conditions")
        .oneOf([true], "You must agree to the Terms and Conditions"),
    }),
  ];

  const verifyReferralCode = async (referralCode) => {
    try {
      const response = await axios.post(
        `${base_url}v1/user/verifyReferralCode`,
        {
          referralCode,
        },
      );
      setReferralData(response.data.data);
    } catch (error) {
      console.error("Error verifying referral code:", error);
      setReferralData(null);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (referralCodeInput && step === 2) {
        verifyReferralCode(referralCodeInput);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [referralCodeInput, step]);

  const handleNextStep = async (values, setSubmitting) => {
    if (step === 0) {
      setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      setStep(3);
    }
    setSubmitting(false);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      dialogClassName="modal-90w md:modal-lg"
    >
      <Modal.Body className="p-4 md:p-6">
        <div className="relative bg-white rounded-lg">
          <button
            onClick={onHide}
            className="absolute top-2 right-2 bg-transparent border-0"
          >
            <FaTimes size={16} color="#666" />
          </button>
          {isRegistered ? (
            <div className="text-center py-6">
              <h2 className="text-lg md:text-xl font-bold text-green-600">
                Registration Successful
              </h2>
              <svg
                className="w-10 h-10 mx-auto text-green-600 mt-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          ) : (
            <>
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  phone: "",
                  vendorType: "",
                  resAddress_line: "",
                  resCountry: "",
                  resState: "",
                  resCity: "",
                  resDistrict: "",
                  resPincode: "",
                  correAddress_line: "",
                  correCountry: "",
                  correState: "",
                  correCity: "",
                  correDistrict: "",
                  correPincode: "",
                  dob: "",
                  gender: "",
                  profilePhoto: null,
                  businessName: "",
                  gstNo: "",
                  bankDetails: {
                    holderName: "",
                    bankName: "",
                    accountNo: "",
                    ifscCode: "",
                  },
                  aadharCard: null,
                  aadharCardNo: "", // New field for Aadhar Card Number
                  panCard: null,
                  panCardNo: "", // New field for PAN Card Number
                  referralCode: referralCodeInput,
                  agreement: false,
                }}
                validationSchema={validationSchema[step]}
                onSubmit={async (values, { setSubmitting, resetForm }) => {
                  if (step < formSteps.length - 1) {
                    handleNextStep(values, setSubmitting);
                    return;
                  }
                  try {
                    const formDataToSend = new FormData();
                    formDataToSend.append("name", values.name);
                    formDataToSend.append("email", values.email);
                    formDataToSend.append("phone", values.phone);
                    formDataToSend.append(
                      "address_line",
                      values.resAddress_line,
                    );
                    formDataToSend.append("country", values.resCountry);
                    formDataToSend.append("state", values.resState);
                    formDataToSend.append("city", values.resCity);
                    formDataToSend.append("district", values.resDistrict);
                    formDataToSend.append("pincode", values.resPincode);
                    formDataToSend.append(
                      "correAddress_line",
                      values.correAddress_line,
                    );
                    formDataToSend.append("correCountry", values.correCountry);
                    formDataToSend.append("correState", values.correState);
                    formDataToSend.append("correCity", values.correCity);
                    formDataToSend.append(
                      "correDistrict",
                      values.correDistrict,
                    );
                    formDataToSend.append("correPincode", values.correPincode);
                    formDataToSend.append("dob", values.dob);
                    formDataToSend.append("gender", values.gender);
                    formDataToSend.append("vendorType", values.vendorType);
                    formDataToSend.append("profilePhoto", values.profilePhoto);
                    if (values.businessName) {
                      formDataToSend.append(
                        "businessName",
                        values.businessName,
                      );
                    }
                    if (values.gstNo) {
                      formDataToSend.append("gstNo", values.gstNo);
                    }
                    formDataToSend.append(
                      "holderName",
                      values.bankDetails.holderName || "",
                    );
                    formDataToSend.append(
                      "accountNo",
                      values.bankDetails.accountNo || "",
                    );
                    formDataToSend.append(
                      "ifscCode",
                      values.bankDetails.ifscCode || "",
                    );
                    formDataToSend.append(
                      "bankName",
                      values.bankDetails.bankName || "",
                    );
                    if (values.aadharCard && values.aadharCardNo) {
                      formDataToSend.append("aadharCard", values.aadharCard);
                      formDataToSend.append(
                        "aadharCardNo",
                        values.aadharCardNo,
                      ); // Append new field
                    }
                    formDataToSend.append("panCard", values.panCard);
                    formDataToSend.append("panCardNo", values.panCardNo); // Append new field
                    if (values.referralCode) {
                      formDataToSend.append("referredBy", referralData._id);
                    }
                    formDataToSend.append("agreement", values.agreement);

                    const url = `${base_url}v1/vendor/userVendorSignUp`;
                    const response = await axios.post(url, formDataToSend, {
                      headers: {
                        "Content-Type": "multipart/form-data",
                      },
                    });
                    if (response.data?.success) {
                      setIsRegistered(true);
                      resetForm();

                      setTimeout(() => {
                        onHide();
                        toast.success("vendor registration successful");
                      }, 1800);
                    } else {
                      throw new Error(
                        response.data?.message || "Registration failed",
                      );
                    }
                  } catch (error) {
                    console.error("Registration error:", error);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, setFieldValue, values, errors }) => {
                  return (
                    <Form className="flex flex-col gap-4 font-['Poppins']">
                      <div className="text-center mb-4">
                        <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                          Vendor Sign Up
                        </h3>
                        <p className="text-gray-500 text-xs md:text-sm">
                          {step === 0
                            ? "Fill in your details to sign up as a Partner"
                            : step === 1
                              ? "Enter bank details and KYC documents (Pan Card Only required)"
                              : step === 2
                                ? "Enter referral code (optional)"
                                : "Review and agree to the terms and conditions (Required)"}
                        </p>
                      </div>
                      {step === 0 && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Name <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="name"
                                placeholder="Enter Name"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Email <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="email"
                                name="email"
                                placeholder="Enter Email"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Phone <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="phone"
                                placeholder="Enter Phone Number"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="phone"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Date of Birth{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="date"
                                name="dob"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                max={new Date().toISOString().split("T")[0]}
                              />
                              <ErrorMessage
                                name="dob"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Gender <span className="text-red-500">*</span>
                              </label>
                              <Field
                                as="select"
                                name="gender"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                              >
                                <option value="">Select Gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </Field>
                              <ErrorMessage
                                name="gender"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Vendor Type
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                as="select"
                                name="vendorType"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                              >
                                <option value="">Select Vendor Type</option>
                                <option value="independent">Independent</option>
                                <option value="dependent">dependent</option>
                              </Field>
                              <ErrorMessage
                                name="vendorType"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                          </div>
                          {/* Dynamic Description Box */}
                          {values.vendorType === "independent" && (
                            <div className="mt-3 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg">
                              <strong>Independent Vendor:</strong> You will sell
                              products directly to customers through our
                              platform. The company will deduct a commission on
                              each successful order.
                            </div>
                          )}

                          {values.vendorType === "dependent" && (
                            <div className="mt-3 p-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg">
                              <strong>Dependent Vendor:</strong> You will supply
                              products to our company. The company will purchase
                              stock from you and handle customer sales.
                            </div>
                          )}
                          <div className="mt-3">
                            <h3 className="text-base md:text-lg font-semibold mb-2">
                              Residential Address{" "}
                              <span className="text-red-500">*</span>
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Street Address{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="resAddress_line"
                                  placeholder="Enter Street Address"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="resAddress_line"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  City <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="resCity"
                                  placeholder="Enter City"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="resCity"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  District{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="resDistrict"
                                  placeholder="Enter District"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="resDistrict"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Pincode{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="resPincode"
                                  placeholder="Enter Pincode"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="resPincode"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Country{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="resCountry"
                                  placeholder="Enter Country"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="resCountry"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  State <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="resState"
                                  placeholder="Enter State"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="resState"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                            </div>
                          </div>
                          {/* Checkbox */}
                          <div className="mt-3">
                            <label className="flex items-center text-sm font-medium text-gray-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={useSameAddress}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setUseSameAddress(checked);
                                  if (checked) {
                                    setFieldValue(
                                      "correAddress_line",
                                      values.resAddress_line || "",
                                    );
                                    setFieldValue(
                                      "correCountry",
                                      values.resCountry || "",
                                    );
                                    setFieldValue(
                                      "correState",
                                      values.resState || "",
                                    );
                                    setFieldValue(
                                      "correCity",
                                      values.resCity || "",
                                    );
                                    setFieldValue(
                                      "correDistrict",
                                      values.resDistrict || "",
                                    );
                                    setFieldValue(
                                      "correPincode",
                                      values.resPincode || "",
                                    );
                                  } else {
                                    // Optional: clear correspondence fields when unchecked
                                    setFieldValue("correAddress_line", "");
                                    setFieldValue("correCountry", "");
                                    setFieldValue("correState", "");
                                    setFieldValue("correCity", "");
                                    setFieldValue("correDistrict", "");
                                    setFieldValue("correPincode", "");
                                  }
                                }}
                                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mr-2"
                              />
                              <span>
                                Same as Residential Address (for Correspondence
                                / Office Address)
                              </span>
                            </label>
                          </div>
                          <div className="mt-3">
                            <h3 className="text-base md:text-lg font-semibold mb-2">
                              Correspondence / Office Address{" "}
                              <span className="text-red-500">*</span>
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Street Address{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="correAddress_line"
                                  placeholder="Enter Street Address"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="correAddress_line"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  City <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="correCity"
                                  placeholder="Enter City"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="correCity"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  District{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="correDistrict"
                                  placeholder="Enter District"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="correDistrict"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Pincode{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="correPincode"
                                  placeholder="Enter Pincode"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="correPincode"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  Country{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="correCountry"
                                  placeholder="Enter Country"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="correCountry"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs md:text-xs font-semibold">
                                  State <span className="text-red-500">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="correState"
                                  placeholder="Enter State"
                                  className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                />
                                <ErrorMessage
                                  name="correState"
                                  component="div"
                                  className="text-red-500 text-xs mt-1"
                                />
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs md:text-xs font-semibold">
                              Profile Photo{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <button
                              type="button"
                              className={`w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm hover:bg-green-700 transition-colors duration-200`}
                              onClick={() =>
                                document.getElementById("profilePhoto").click()
                              }
                            >
                              Upload
                            </button>
                            <input
                              type="file"
                              id="profilePhoto"
                              name="profilePhoto"
                              accept="image/*"
                              className="hidden"
                              onChange={(event) => {
                                setFieldValue(
                                  "profilePhoto",
                                  event.currentTarget.files[0],
                                );
                              }}
                            />
                            {values.profilePhoto && (
                              <div className="mt-1 text-xs text-green-600">
                                {values.profilePhoto.name || "File uploaded"}
                              </div>
                            )}
                            <ErrorMessage
                              name="profilePhoto"
                              component="div"
                              className="text-red-500 text-xs mt-1"
                            />
                          </div>
                        </>
                      )}
                      {step === 1 && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                PAN Card Number{" "}
                                <span className="text-red-500">*</span>
                              </label>
                              <Field
                                type="text"
                                name="panCardNo"
                                placeholder="Enter PAN Card Number"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                              />
                              <ErrorMessage
                                name="panCardNo"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                PAN Card <span className="text-red-500">*</span>
                              </label>
                              <button
                                type="button"
                                className="w-full mt-1 p-2 bg-[#00C70D] text-white rounded-lg text-sm hover:bg-green-700 transition-colors duration-200"
                                onClick={() =>
                                  document.getElementById("panCard").click()
                                }
                              >
                                Upload
                              </button>
                              <input
                                type="file"
                                id="panCard"
                                name="panCard"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="hidden"
                                onChange={(event) =>
                                  setFieldValue(
                                    "panCard",
                                    event.currentTarget.files[0],
                                  )
                                }
                              />
                              {values.panCard && (
                                <div className="mt-1 text-xs text-green-600">
                                  {values.panCard.name || "File uploaded"}
                                </div>
                              )}
                              <ErrorMessage
                                name="panCard"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      {step === 2 && (
                        <>
                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <label className="block text-xs md:text-xs font-semibold">
                                Referral Code (Optional)
                              </label>
                              <Field
                                type="text"
                                name="referralCode"
                                placeholder="Enter Referral Code"
                                className="modern-input w-full mt-1 p-2 text-sm border-gray-300 bg-gray-100 rounded-lg focus:ring-2 focus:ring-green-500"
                                onChange={(e) => {
                                  setFieldValue("referralCode", e.target.value);
                                  setReferralCodeInput(e.target.value);
                                }}
                              />
                              <ErrorMessage
                                name="referralCode"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                            {referralData ? (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Name
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      referralData.name ||
                                      `${referralData.firstname} ${referralData.lastname}`
                                    }
                                    disabled
                                    className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded-lg opacity-75 cursor-not-allowed"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs md:text-xs font-semibold">
                                    Mobile
                                  </label>
                                  <input
                                    type="text"
                                    value={
                                      referralData.phone || referralData.mobile
                                    }
                                    disabled
                                    className="modern-input w-full mt-1 p-3 text-sm border-gray-300 bg-gray-100 rounded-lg opacity-75 cursor-not-allowed"
                                  />
                                </div>
                              </div>
                            ) : (
                              <>
                                {values.referralCode && (
                                  <div className="text-red-500 text-xs mt-1">
                                    Invalid Referral Code
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                      {step === 3 && (
                        <>
                          <div className="mt-4">
                            <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#00C70D] to-[#005AD0] bg-clip-text text-transparent">
                              Terms and Conditions
                            </h3>
                            <p className="text-gray-500 text-xs md:text-sm mb-4">
                              Terms and Conditions for Channel Partner
                              Enrollment
                            </p>
                            <div className="max-w-3xl mx-auto p-6 bg-white rounded-2xl shadow-lg border border-gray-100">
                              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                Channel Partner Agreement Terms & Conditions
                              </h2>
                              <div className="max-h-[400px] overflow-y-auto p-4 bg-gray-50 rounded-lg scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-100 scrollbar-thumb-rounded-full">
                                <div className="text-gray-700 font-['Poppins'] text-sm leading-relaxed">
                                  <p className="mb-4 text-gray-600">
                                    This{" "}
                                    <span className="font-semibold">
                                      Agreement
                                    </span>{" "}
                                    governs the enrollment and participation of
                                    individuals or entities (“Channel Partner”)
                                    in the distribution of the Global Digital
                                    Health Smart Card, offered by LifeOnPlus
                                    (“Company”). By enrolling as a Channel
                                    Partner, you agree to comply with and be
                                    bound by the following terms:
                                  </p>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      1
                                    </span>
                                    Eligibility & Enrollment
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      Channel Partner must be 18 years of age or
                                      above and legally competent to enter into
                                      this Agreement.
                                    </li>
                                    <li>
                                      Enrollment is subject to approval by the
                                      Company, and the Company reserves the
                                      right to reject or terminate any
                                      enrollment without assigning reasons.
                                    </li>
                                    <li>
                                      The Channel Partner must provide accurate
                                      details including PAN, GST (if
                                      applicable), and banking details for
                                      settlements.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      2
                                    </span>
                                    Roles & Responsibilities
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      The Channel Partner is authorized to
                                      promote, market, and distribute Global
                                      Digital Health Smart Cards strictly as per
                                      Company guidelines.
                                    </li>
                                    <li>
                                      Misrepresentation of product benefits,
                                      features, or unauthorized claims is
                                      strictly prohibited.
                                    </li>
                                    <li>
                                      The Channel Partner shall not engage in
                                      activities that may harm the reputation or
                                      goodwill of the Company.
                                    </li>
                                    <li>
                                      All marketing, training, and branding
                                      materials shall be provided/approved by
                                      the Company.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      3
                                    </span>
                                    Financial Terms
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      Channel Partner earnings shall be based on
                                      the Company’s distribution margin,
                                      referral structure, and wallet system as
                                      communicated from time to time.
                                    </li>
                                    <li>
                                      All settlements will be made net of
                                      applicable TDS, service fees, GST, and
                                      other statutory deductions.
                                    </li>
                                    <li>
                                      No fixed returns, interest, or guaranteed
                                      income is offered. Earnings depend solely
                                      on sales and business performance.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      4
                                    </span>
                                    Intellectual Property
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      The Channel Partner is granted a
                                      non-exclusive, non-transferable right to
                                      use the Company’s name, logo, and
                                      promotional materials solely for business
                                      purposes.
                                    </li>
                                    <li>
                                      Any misuse of Company’s intellectual
                                      property shall result in termination and
                                      legal action.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      5
                                    </span>
                                    Confidentiality
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      The Channel Partner shall maintain strict
                                      confidentiality of all Company data,
                                      agreements, pricing structures, and
                                      business models.
                                    </li>
                                    <li>
                                      Sharing or misusing Company information
                                      with competitors or third parties is
                                      prohibited.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      6
                                    </span>
                                    Termination
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      The Company reserves the right to suspend
                                      or terminate the partnership at any time
                                      in case of non-compliance with this
                                      Agreement.
                                    </li>
                                    <li>
                                      Upon termination, all rights to represent
                                      the Company or distribute products shall
                                      cease immediately.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      7
                                    </span>
                                    Dispute Resolution & Jurisdiction
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      Any disputes arising out of or in
                                      connection with this Agreement shall first
                                      be attempted to be resolved amicably
                                      through mutual discussion.
                                    </li>
                                    <li>
                                      Failing amicable settlement, disputes
                                      shall be referred to Arbitration under the
                                      Arbitration and Conciliation Act, 1996.
                                    </li>
                                    <li>
                                      The Arbitrator shall be appointed solely
                                      by the Company, and the decision of the
                                      Arbitrator shall be final and binding.
                                    </li>
                                    <li>
                                      The courts at Bangalore, Karnataka, shall
                                      have exclusive jurisdiction.
                                    </li>
                                  </ul>
                                  <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3 flex items-center">
                                    <span className="inline-block w-6 h-6 bg-blue-500 text-white rounded-full text-center mr-2">
                                      8
                                    </span>
                                    Governing Law
                                  </h3>
                                  <ul className="list-disc pl-6 space-y-2">
                                    <li>
                                      This Agreement shall be governed by and
                                      construed in accordance with the laws of
                                      India.
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                            <div className="mt-4">
                              <label className="flex items-center text-sm md:text-base font-['Poppins'] max-w-full">
                                <Field
                                  type="checkbox"
                                  name="agreement"
                                  className="mr-2 h-4 w-4"
                                />
                                <span>
                                  I agree to the Terms and Conditions{" "}
                                  <span className="text-red-500 font-semibold">
                                    <span className="text-red-500">*</span>
                                  </span>{" "}
                                  and understand that this investment secures
                                  exclusive rights for the selected region,
                                  subject to verification by LifeOnPlus. I
                                  confirm all information provided is true to
                                  the best of my knowledge.
                                </span>
                              </label>
                              <ErrorMessage
                                name="agreement"
                                component="div"
                                className="text-red-500 text-xs mt-1"
                              />
                            </div>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between mt-4">
                        {step > 0 && (
                          <Button
                            onClick={() => setStep(step - 1)}
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-[#1DA1F2] text-white font-['Poppins'] rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
                          >
                            Back
                          </Button>
                        )}
                        <Button
                          type="submit"
                          disabled={
                            isSubmitting || (step === 3 && !values.agreement)
                          }
                          className="px-4 py-2 bg-[#00C70D] text-white font-['Poppins'] rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:bg-gray-300 disabled:text-gray-600"
                        >
                          {step === formSteps.length - 1 ? "Submit" : "Next"}
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VendorSignup;
